/* eslint-disable no-unused-vars */
import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useEffect, useState } from "react";
import Dropzone from "react-dropzone";
import { useLocation, useNavigate } from "react-router-dom";
import formatError from "../../../services/FormatError";
import {
  generateUploadUrl,
  SubmitFile,
  uploadFile,
} from "../../../services/BusinessFileUploadService";
import { fetchRequiredFileList } from "../../../services/BusinessFileUploadService";
import DocumentViewer from "../../components/UIComponents/DocumentViewer";
import { getBase64 } from "../Registration";
import { fetchBusinessDetails } from "../../../services/BusinessService";
import config from "../../../config";
import swal from "sweetalert";
import { useTranslation } from "react-i18next";
import { Col, Row, Card, Modal, Button } from "react-bootstrap";
import { Folder } from "lucide-react";

const UploadBusinessFiles = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const businessId = useLocation()?.state?.businessId;
  const [localImages, setLocalImages] = useState([]);
  const [localVideos, setLocalVideos] = useState([]);
  const [localDocuments, setLocalDocuments] = useState([]);
  const [imageUploading, setImageUploading] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const [documentUploading, setDocumentUploading] = useState(false);
  const [viewFile, setViewFile] = useState({ show: false, url: "" });
  const [requiredFiles, setRequiredFiles] = useState([]);
  const [uploadsBySlug, setUploadsBySlug] = useState({});
  const [uploadingBySlug, setUploadingBySlug] = useState({});
  const [categoryById, setCategoryById] = useState({});
  const [activeCategory, setActiveCategory] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [businessDetails, setBusinessDetails] = useState(null);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [selectedOwnerId, setSelectedOwnerId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchRequiredFileList()
      .then((res) => {
        const list = res.data?.result || [];
        setRequiredFiles(Array.isArray(list) ? list : []);
        const byId = {};
        (Array.isArray(list) ? list : []).forEach((c) => {
          byId[c.id] = c;
        });
        setCategoryById(byId);
      })
      .catch((error) => {
        formatError(error.response?.data?.error);
      });
  }, []);

  useEffect(() => {
    if (!businessId) return;
    fetchBusinessDetails(businessId)
      .then((res) => {
        const details = res.data?.result || {};
        setBusinessDetails(details);
        const docs = Array.isArray(details?.documents) ? details.documents : [];
        const preloaded = {};
        docs.forEach((doc) => {
          if (
            doc?.type === "DOCUMENT" &&
            doc?.categoryid &&
            categoryById[doc.categoryid]
          ) {
            const cat = categoryById[doc.categoryid];
            const slug = cat.slug;
            const url = `${config?.appUrl}${config.routes.fileViewUrl}?key=${doc?.key}&type=DOCUMENT`;

            // Attempt to retrieve ownerName from various fields
            const ownerNameFromDoc =
              doc?.ownername || doc?.owner_name || doc?.ownerName || null;
            // Attempt to retrieve year from common fields (if backend stored it)
            const yearFromDoc =
              doc?.year ||
              doc?.document_year ||
              doc?.meta?.year ||
              doc?.year_number ||
              null;

            const docInfo = {
              url,
              ownerName: ownerNameFromDoc,
              year: yearFromDoc ? Number(yearFromDoc) : null,
              documentId: doc?.id || null,
            };
            preloaded[slug] = preloaded[slug]
              ? [...preloaded[slug], docInfo]
              : [docInfo];
          }
        });
        setUploadsBySlug((prev) => ({ ...prev, ...preloaded }));
      })
      .catch((error) => {
        formatError(error.response?.data?.error);
      });
  }, [businessId, categoryById]);

  const fileHandler = async (files, type) => {
    const file = files[0];
    if (file) {
      if (type === "image") {
        setImageUploading(true);
      }
      if (type === "video") {
        setVideoUploading(true);
      }
      if (type === "document") {
        setDocumentUploading(true);
      }
      const sizeLimit = 500 * 1024 * 1024; // 500MB
      const fileType = file.type;
      if (file.size > sizeLimit) {
        swal("Oops", t("hmaxfile"), "error", { button: t("htry") });
        setImageUploading(false);
        setVideoUploading(false);
        setDocumentUploading(false);
      } else if (
        fileType === "image/jpeg" ||
        fileType === "image/jpg" ||
        fileType === "image/png" ||
        fileType === "video/mp4" ||
        fileType === "application/pdf"
      ) {
        let payload = new FormData();
        payload.append("file", file);
        payload.append("entity", "BUSINESS");
        payload.append("entityid", Number(businessId));
        await uploadedFileHandler(payload, file);
      } else {
        setImageUploading(false);
        setVideoUploading(false);
        setDocumentUploading(false);
        swal(
          "Oops",
          "Please select a valid file. (images jpg, jpeg, png) (video mp4 only) (document pdf only)",
          "error",
          { button: "Try Again!" }
        );
      }
    }
  };

  const requiredFileHandler = async (
    files,
    category,
    ownerName = null,
    year = 1
  ) => {
    const file = files[0];
    if (!file) return;
    if (!ownerName) {
      swal(t("hselectshareholder"), "warning");
      return;
    }

    const ownersCount = businessDetails?.owners?.length || 1;
    const totalAllowed = ownersCount * 3;
    const previews = uploadsBySlug[category.slug] || [];
    if (previews.length >= totalAllowed) {
      swal("Warning", t("hmaxReachedCategory"), "warning");
      return;
    }

    setUploadingBySlug((prev) => ({ ...prev, [category.slug]: true }));

    let payload = new FormData();
    payload.append("file", file);
    payload.append("entity", "BUSINESS");
    payload.append("entityid", Number(businessId));
    payload.append("categoryid", category.id);

    // Append owner name using multiple common keys so backend picks what it expects
    payload.append("ownername", String(ownerName));
    payload.append("owner_name", String(ownerName));

    // Append year using multiple common keys so backend picks what it expects
    payload.append("year", Number(year));

    try {
      const res = await uploadFile(payload);
      const docInfo = {
        url: URL.createObjectURL(file),
        ownerName,
        year,
        documentId: res.data?.result?.id || null,
      };
      setUploadsBySlug((prev) => {
        const existing = prev[category.slug] || [];
        return { ...prev, [category.slug]: [...existing, docInfo] };
      });
      swal("Success", t("huploadingsuccessfull"), "success");
    } catch (error) {
      formatError(error?.response?.data?.error);
    }

    setUploadingBySlug((prev) => ({ ...prev, [category.slug]: false }));
  };

  const uploadedFileHandler = async (data, file) => {
    await uploadFile(data)
      .then((res) => {
        if (res.data?.result?.type === "IMAGE") {
          const imageUrl = URL.createObjectURL(file);
          setLocalImages((prev) => [...prev, imageUrl]);
          setImageUploading(false);
        }
        if (res.data?.result?.type === "VIDEO") {
          const videoUrl = URL.createObjectURL(file);
          setLocalVideos((prev) => [...prev, videoUrl]);
          setVideoUploading(false);
        }
        if (res.data?.result?.type === "DOCUMENT") {
          setLocalDocuments((prev) => [...prev, file]);
          setDocumentUploading(false);
        }
        swal("Success", t("huploadingsuccessfull"), "success");
      })
      .catch((error) => {
        setImageUploading(false);
        setVideoUploading(false);
        setDocumentUploading(false);
        formatError(error.response?.data?.error);
      });
  };

  // Helper: count how many distinct years this owner uploaded for this category
  const countOwnerYearsForCategory = (ownerName, categorySlug) => {
    if (!ownerName) return 0;
    const docs = uploadsBySlug[categorySlug] || [];
    const yearsSet = new Set();
    docs.forEach((d) => {
      if (d?.ownerName === ownerName && d?.year) {
        yearsSet.add(Number(d.year));
      }
    });
    return yearsSet.size;
  };

  // Helper: check if owner has uploaded for a specific year in this category
  const ownerHasUploadedYear = (ownerName, categorySlug, year) => {
    if (!ownerName) return false;
    const docs = uploadsBySlug[categorySlug] || [];
    return docs.some(
      (d) => d?.ownerName === ownerName && Number(d?.year) === Number(year)
    );
  };

  return (
    <div className="card">
      <div className="card-header d-sm-flex d-block">
        <div className="d-flex me-auto mb-sm-0">
          <span
            style={{ fontSize: "18px", cursor: "pointer" }}
            className="d-flex justify-content-start align-items-center me-3"
            onClick={() => navigate(-1)}
          >
            <i className="fa fa-arrow-left"></i>
          </span>
          <h3 className="card-title">{t("sidebarItemDocuments")}</h3>
        </div>
      </div>

      <div className="p-4 border-bottom">
        <h3 className="text-xl font-medium">{t("categories")}</h3>
        <div>
          <Row className="g-4">
            {requiredFiles?.map((req) => {
              const previews = uploadsBySlug[req.slug] || [];
              const previewCount = Array.isArray(previews)
                ? previews.length
                : 0;
              // Option A: total allowed = owners.length * 3
              const ownersCount = Array.isArray(businessDetails?.owners)
                ? businessDetails.owners.length
                : 1;
              const totalAllowed = ownersCount * 3;
              const isUploading = uploadingBySlug[req.slug];
              const missing = previewCount === 0;
              const handleOpenCategoryModal = () => {
                setActiveCategory(req);
                setShowCategoryModal(true);
                setSelectedOwner(null);
              };
              return (
                <Col key={req.id} xs={12} sm={6} md={3}>
                  <Card
                    className="rounded-2xl shadow-sm cursor-pointer p-4 mb-4"
                    onClick={handleOpenCategoryModal}
                  >
                    <Card.Body className="space-y-8 p-0">
                      <div className="d-flex justify-content-between">
                        <Folder className="w-14 h-14 mx-auto" />{" "}
                        {missing && (
                          <span className="badge badge-danger badge-pill">
                            {t("hmis")}
                          </span>
                        )}
                      </div>
                      <p className="font-weight-bold mt-2">{req.name}</p>
                      <small className="text-center text-muted text-sm text-gray-600">
                        {/* show updated capacity (owners.length * 3) */}
                        {totalAllowed} {t("hnbreFichier")}
                      </small>
                    </Card.Body>
                  </Card>
                  <div className="d-flex align-items-center mt-2"></div>
                </Col>
              );
            })}
          </Row>
        </div>
      </div>

      <Row>
        <Col xs={12} sm={12} md={4}>
          <div className="p-4">
            <h4>{t("huploadimage")}</h4>
            <div>
              <div className="d-flex align-items-center">
                {localImages?.length !== 0 && (
                  <div className="d-flex">
                    {localImages?.map((imageUrl, index) => (
                      <div
                        key={index}
                        style={{
                          width: "200px",
                          height: "120px",
                          marginRight: "10px",
                        }}
                      >
                        <img
                          className=""
                          src={imageUrl}
                          alt=""
                          style={{ width: "200px", height: "120px" }}
                        />
                      </div>
                    ))}
                    {imageUploading && (
                      <div
                        style={{
                          width: "200px",
                          height: "120px",
                          marginRight: "10px",
                          position: "relative",
                        }}
                      >
                        <div
                          className="d-flex justify-content-center"
                          style={{
                            position: "absolute",
                            width: "100%",
                            height: "100%",
                            paddingTop: "20%",
                            backgroundColor: "#00000020",
                          }}
                        >
                          <div className="spinner-border" role="status">
                            <span className="visually-hidden">
                              {t("hloading")}
                            </span>
                          </div>
                          <p className="mt-1 ms-2">
                            {t("voiceNote_uploading")}
                          </p>
                        </div>
                        <div
                          className=""
                          alt=""
                          style={{
                            width: "200px",
                            height: "120px",
                            backgroundColor: "#00000050",
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}

                <Dropzone
                  onDrop={(file) => fileHandler(file, "image")}
                  accept={{ "image/*": [".jpeg", ".jpg", ".png"] }}
                  maxFiles={1}
                >
                  {({ getRootProps, getInputProps }) => (
                    <div
                      className={`${
                        localImages?.length === 0 ? "container" : ""
                      }`}
                      style={{
                        width: localImages?.length === 0 ? "500px" : "300px",
                        border: "1px dashed #00000050",
                        borderRadius: "10px",
                        cursor: "pointer",
                      }}
                    >
                      <div
                        {...getRootProps({
                          className: "dropzone",
                          onDrop: (event) => event.stopPropagation(),
                        })}
                      >
                        <input {...getInputProps()} />
                        <p className="text-center mt-4">
                          {t("hclickdragImage")}
                        </p>
                        <p className="text-center ">{t("himagemore")}</p>
                      </div>
                    </div>
                  )}
                </Dropzone>
              </div>
            </div>
          </div>
        </Col>

        <Col xs={12} sm={12} md={4}>
          <div className="p-4 ">
            <h4>{t("huploadVideo")}</h4>
            <div>
              <div className="d-flex align-items-center">
                {localVideos?.length !== 0 && (
                  <div className="d-flex">
                    {localVideos?.map((videoUrl, index) => (
                      <div
                        key={index}
                        style={{
                          width: "200px",
                          height: "120px",
                          marginRight: "10px",
                        }}
                      >
                        <video
                          className=""
                          src={videoUrl}
                          alt=""
                          style={{ width: "200px", height: "120px" }}
                          controls
                        ></video>
                      </div>
                    ))}
                    {videoUploading && (
                      <div
                        style={{
                          width: "200px",
                          height: "120px",
                          marginRight: "10px",
                          position: "relative",
                        }}
                      >
                        <div
                          className="d-flex justify-content-center"
                          style={{
                            position: "absolute",
                            width: "100%",
                            height: "100%",
                            paddingTop: "20%",
                            backgroundColor: "#00000020",
                          }}
                        >
                          <div className="spinner-border" role="status">
                            <span className="visually-hidden">
                              {t("hloading")}
                            </span>
                          </div>
                          <p className="mt-1 ms-2">
                            {t("voiceNote_uploading")}
                          </p>
                        </div>
                        <div
                          className=""
                          alt=""
                          style={{
                            width: "200px",
                            height: "120px",
                            backgroundColor: "#00000050",
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}

                <Dropzone
                  onDrop={(file) => fileHandler(file, "video")}
                  accept={{ "video/*": [".mp4"] }}
                  maxFiles={1}
                >
                  {({ getRootProps, getInputProps }) => (
                    <div
                      className={`${
                        localVideos?.length === 0 ? "container" : ""
                      }`}
                      style={{
                        width: localVideos?.length === 0 ? "500px" : "300px",
                        border: "1px dashed #00000050",
                        borderRadius: "10px",
                        cursor: "pointer",
                      }}
                    >
                      <div
                        {...getRootProps({
                          className:
                            "dropzone d-flex align-items-center justify-content-center",
                          onDrop: (event) => event.stopPropagation(),
                        })}
                      >
                        <input {...getInputProps()} />
                        <p className="text-center mt-4">
                          {t("hclickdragvideo")}
                        </p>
                        <p className="text-center ">{t("hvideomore")}</p>
                      </div>
                    </div>
                  )}
                </Dropzone>
              </div>
            </div>
          </div>
        </Col>

        <Col xs={12} sm={12} md={4}>
          <div className="p-4 ">
            <h4>{t("huploaddocument")}</h4>
            <div>
              <div className="d-flex align-items-center">
                {localDocuments?.length !== 0 && (
                  <div className="d-flex">
                    {localDocuments?.map((docUrl, index) => (
                      <div className="d-flex me-3" key={index}>
                        <div className="d-flex flex-column justify-content-center">
                          <button
                            type="button"
                            onClick={() =>
                              setViewFile({ show: true, url: docUrl })
                            }
                            className="flex justify-center max-w-[100px] max-h-8 border-brand"
                          >
                            <Icon
                              icon="material-symbols:file-present-outline"
                              width="50"
                            />
                          </button>
                          <p
                            onClick={() =>
                              setViewFile({ show: true, url: docUrl })
                            }
                            style={{
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                              maxWidth: "80px",
                              cursor: "pointer",
                            }}
                          >
                            Image1
                          </p>
                        </div>
                        <Icon
                          icon="ic:outline-delete"
                          className=" text-red "
                          fontSize={20}
                          style={{ cursor: "pointer" }}
                        />
                      </div>
                    ))}
                    {documentUploading && (
                      <div
                        style={{
                          width: "200px",
                          height: "120px",
                          marginRight: "10px",
                          position: "relative",
                        }}
                      >
                        <div
                          className="d-flex justify-content-center"
                          style={{
                            position: "absolute",
                            width: "100%",
                            height: "100%",
                            paddingTop: "20%",
                            backgroundColor: "#00000020",
                          }}
                        >
                          <div className="spinner-border" role="status">
                            <span className="visually-hidden">
                              {t("hloading")}
                            </span>
                          </div>
                          <p className="mt-1 ms-2">
                            {t("voiceNote_uploading")}
                          </p>
                        </div>
                        <div
                          className=""
                          alt=""
                          style={{
                            width: "200px",
                            height: "120px",
                            backgroundColor: "#00000050",
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}

                <Dropzone
                  onDrop={(file) => fileHandler(file, "document")}
                  accept={{ "document/*": [".pdf"] }}
                  maxFiles={1}
                >
                  {({ getRootProps, getInputProps }) => (
                    <div
                      className={`${
                        localDocuments?.length === 0 ? "container" : ""
                      }`}
                      style={{
                        width: localDocuments?.length === 0 ? "500px" : "300px",
                        border: "1px dashed #00000050",
                        borderRadius: "10px",
                        cursor: "pointer",
                      }}
                    >
                      <div
                        {...getRootProps({
                          className: "dropzone",
                          onDrop: (event) => event.stopPropagation(),
                        })}
                      >
                        <input {...getInputProps()} />
                        <p className="text-center mt-4">
                          {t("hclickdrapdocument")}
                        </p>
                        <p className="text-center ">{t("hdocumentmore")}</p>
                      </div>
                    </div>
                  )}
                </Dropzone>
              </div>
            </div>
          </div>
        </Col>

        {viewFile.url && (
          <DocumentViewer
            filePath={viewFile.url}
            open={viewFile.show}
            handleClose={() => setViewFile({ show: false, url: "" })}
          />
        )}
      </Row>

      {activeCategory && (
        <Modal
          show={showCategoryModal}
          onHide={() => {
            setShowCategoryModal(false);
            setActiveCategory(null);
            setSelectedOwner(null);
          }}
          centered
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>{activeCategory.name}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col md={6} className="">
                {Array.isArray(businessDetails?.owners) &&
                  businessDetails.owners.length > 0 && (
                    <div className="mb-3">
                      <h6 className="mb-2">
                        {t("hpartner") || "Actionnaires"}
                      </h6>
                      <div className="mb-3">
                        <label className="form-label">
                          {t("hselectShareholdfordocument")}
                        </label>
                        <select
                          className="form-select"
                          value={selectedOwner ?? ""}
                          onChange={(e) => {
                            setSelectedOwner(e.target.value || null);
                          }}
                        >
                          <option value="">
                            {" "}
                            -- {t("selectionShareholder")} --{" "}
                          </option>
                          {businessDetails.owners.map((owner, index) => {
                            const ownerName =
                              owner?.name ||
                              owner?.fullName ||
                              `Actionnaire ${index + 1}`;
                            // disable if owner uploaded all 3 years for this category
                            const ownerYearsCount = countOwnerYearsForCategory(
                              ownerName,
                              activeCategory.slug
                            );
                            const disableOwner = ownerYearsCount >= 3;
                            return (
                              <option
                                key={index}
                                value={ownerName}
                                disabled={disableOwner}
                              >
                                {ownerName}{" "}
                                {disableOwner
                                  ? `(${
                                      t("hallYearsUploaded") ||
                                      "All years uploaded"
                                    })`
                                  : ""}
                              </option>
                            );
                          })}
                        </select>
                        {selectedOwner && (
                          <small className="text-success mt-1 d-block">
                            <i className="fa fa-check"></i>{" "}
                            {t("hselectedShareholder")} ({selectedOwner})
                          </small>
                        )}
                      </div>
                    </div>
                  )}

                <h6 className="mb-3">{t("huploaddocument")}</h6>
                {uploadingBySlug[activeCategory.slug] && (
                  <div className="d-flex align-items-center mt-3">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">{t("hloading")}</span>
                    </div>
                    <p className="mb-0 ms-2">{t("voiceNote_uploading")}</p>
                  </div>
                )}
              </Col>
              <Col md={6}>
                <h6 className="">{t("hpreview")}</h6>
                <div className="d-flex flex-column gap-2">
                  {(uploadsBySlug[activeCategory.slug] || []).length === 0 && (
                    <p className="text-muted mb-0">{t("hnodocument")}</p>
                  )}
                  {(uploadsBySlug[activeCategory.slug] || []).map(
                    (doc, index) => {
                      // doc expected to be an object with url and ownerName and year
                      const docUrl = typeof doc === "string" ? doc : doc?.url;
                      const ownerName =
                        typeof doc === "object" ? doc?.ownerName : null;
                      const yearLabel =
                        typeof doc === "object" ? doc?.year : null;
                      return (
                        <div
                          key={index}
                          className="d-flex align-items-center justify-content-between border rounded p-2"
                        >
                          <span className="d-flex flex-column gap-1">
                            <span className="d-flex align-items-center gap-2">
                              <Icon
                                icon="material-symbols:file-present-outline"
                                width="28"
                                className="me-2"
                              />{" "}
                              {t("hdocument")} {index + 1}
                            </span>
                            {ownerName && (
                              <small className="text-muted ms-4">
                                <i className="fa fa-user"></i> {t("hpartner")}:{" "}
                                <strong>{ownerName}</strong>
                                {yearLabel && (
                                  <span>
                                    {" "}
                                    - {t("hyearShort") || "Year"} {yearLabel}
                                  </span>
                                )}
                              </small>
                            )}
                          </span>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() =>
                              setViewFile({ show: true, url: docUrl })
                            }
                          >
                            {t("hpreview")}
                          </Button>
                        </div>
                      );
                    }
                  )}
                </div>
              </Col>
            </Row>

            <Row>
              <Row className="g-3">
                {[1, 2, 3].map((year, idx) => {
                  const ownersCount = Array.isArray(businessDetails?.owners)
                    ? businessDetails.owners.length
                    : 1;
                  const totalAllowed = ownersCount * 3;
                  const previews = uploadsBySlug[activeCategory.slug] || [];
                  const previewCount = Array.isArray(previews)
                    ? previews.length
                    : 0;
                  const totalReached = previewCount >= totalAllowed;
                  const ownerAlreadyForYear =
                    selectedOwner &&
                    ownerHasUploadedYear(
                      selectedOwner,
                      activeCategory.slug,
                      year
                    );
                  return (
                    <Col xs={12} md={6} key={year}>
                      <label className="form-label">
                        {t("hyearLabel")} {year}
                      </label>
                      <Dropzone
                        onDrop={(files) =>
                          requiredFileHandler(
                            files,
                            activeCategory,
                            selectedOwner,
                            year
                          )
                        }
                        accept={{ "application/pdf": [".pdf"] }}
                        maxFiles={1}
                        disabled={
                          !selectedOwner || totalReached || ownerAlreadyForYear
                        }
                      >
                        {({ getRootProps, getInputProps, isDragActive }) => (
                          <div
                            {...getRootProps({
                              className:
                                "dropzone d-flex align-items-center justify-content-center",
                              onDrop: (event) => event.stopPropagation(),
                            })}
                            style={{
                              border: "2px dashed #00000050",
                              borderRadius: "10px",
                              padding: "20px",
                              backgroundColor: isDragActive
                                ? "#f8f9fa"
                                : "transparent",
                              cursor:
                                !selectedOwner ||
                                totalReached ||
                                ownerAlreadyForYear
                                  ? "not-allowed"
                                  : "pointer",
                              opacity:
                                !selectedOwner ||
                                totalReached ||
                                ownerAlreadyForYear
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <input {...getInputProps()} />
                            <div className="text-center">
                              <p className="mb-1">
                                {t("hclickdrapdocument")} — {t("hyearShort")}{" "}
                                {year}
                              </p>
                              <p className="mb-0 text-muted">
                                {t("hdocumentmore")}
                              </p>
                              {!selectedOwner && (
                                <p className="mb-0 text-danger mt-2">
                                  <i className="fa fa-warning"></i>{" "}
                                  {t("hselectshareholder")}
                                </p>
                              )}
                              {ownerAlreadyForYear && (
                                <p className="mb-0 text-info mt-2">
                                  <i className="fa fa-check"></i>{" "}
                                  {t("hownerHasYear")}
                                </p>
                              )}
                              {totalReached && (
                                <p className="mb-0 text-danger mt-2">
                                  <i className="fa fa-warning"></i>{" "}
                                  {t("hmaxReachedCategory")}
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </Dropzone>
                    </Col>
                  );
                })}
              </Row>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowCategoryModal(false);
                setActiveCategory(null);
                setSelectedOwner(null);
              }}
            >
              {t("close") || "Close"}
            </Button>
            <Button
              variant="primary"
              onClick={async () => {
                setIsSubmitting(true);
                try {
                  // Récupérer tous les documents uploadés pour cette catégorie
                  const uploadedDocs = uploadsBySlug[activeCategory.slug] || [];
                  if (uploadedDocs.length === 0) {
                    swal(
                      "Warning",
                      t("hnodocument") || "No documents to submit",
                      "warning"
                    );
                    setIsSubmitting(false);
                    return;
                  }

                  // If you need to call SubmitFile to finalize submission, you can map and call here.
                  // For now, we show success and refresh details as before.
                  swal(
                    "Success",
                    t("hsubmitdocument") || "Documents submitted successfully",
                    "success"
                  );
                  setShowCategoryModal(false);
                  setActiveCategory(null);
                  setSelectedOwner(null);

                  // Refresh business details & re-populate uploadsBySlug from fresh data
                  if (businessId) {
                    fetchBusinessDetails(businessId)
                      .then((res) => {
                        const details = res.data?.result || {};
                        setBusinessDetails(details);
                        const docs = Array.isArray(details?.documents)
                          ? details.documents
                          : [];
                        const preloaded = {};
                        docs.forEach((doc) => {
                          if (
                            doc?.type === "DOCUMENT" &&
                            doc?.categoryid &&
                            categoryById[doc.categoryid]
                          ) {
                            const cat = categoryById[doc.categoryid];
                            const slug = cat.slug;
                            const url = `${config?.appUrl}${config.routes.fileViewUrl}?key=${doc?.key}&type=DOCUMENT`;
                            const ownerName =
                              doc?.ownername ||
                              doc?.owner_name ||
                              doc?.ownerName ||
                              null;
                            const yearFromDoc =
                              doc?.year ||
                              doc?.document_year ||
                              doc?.meta?.year ||
                              null;
                            const docInfo = {
                              url,
                              ownerId: doc?.ownerid || doc?.owner_id || null,
                              ownerName: ownerName,
                              year: yearFromDoc ? Number(yearFromDoc) : null,
                              documentId: doc?.id || null,
                            };
                            preloaded[slug] = preloaded[slug]
                              ? [...preloaded[slug], docInfo]
                              : [docInfo];
                          }
                        });
                        setUploadsBySlug((prev) => ({ ...preloaded }));
                      })
                      .catch((error) => {
                        formatError(error.response?.data?.error);
                      });
                  }
                } catch (error) {
                  formatError(error.response?.data?.error);
                } finally {
                  setIsSubmitting(false);
                }
              }}
              disabled={
                isSubmitting ||
                (uploadsBySlug[activeCategory.slug] || []).length === 0
              }
            >
              {isSubmitting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  {t("hloading") || "Loading..."}
                </>
              ) : (
                t("hsubmit") || "SUBMIT"
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default UploadBusinessFiles;
